import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  downloadCsv,
  getInvoiceQueue,
  markInvoiceDone,
  TInvoiceQueueItem,
} from '../../../requests/opsReports';
import {
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  OriginBadge,
  Pill,
  tableClass,
  tdClass,
  thClass,
  theadClass,
} from '../ui';

const money = (v: number | null) =>
  v == null
    ? '—'
    : v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const LOOKBACKS = [30, 90, 180, 365];

/**
 * What finance still has to act on (T6.07): invoice-directly classes that
 * have run, travel to be invoiced, programs born here or from a deal —
 * each gone from the list once a person marks it done. The finance
 * trigger without QuickBooks; the note on the tab says so.
 */
const InvoiceQueue: React.FC = () => {
  const qc = useQueryClient();
  const [days, setDays] = useState(180);
  const [busy, setBusy] = useState(false);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['opsInvoiceQueue', days],
    queryFn: () => getInvoiceQueue({ days }),
  });

  const done = useMutation({
    mutationFn: (i: TInvoiceQueueItem) =>
      markInvoiceDone(i.kind, i.id, note.trim() || undefined),
    onSuccess: () => {
      showToast('Marked invoiced — recorded with your name', 'success');
      setNoteFor(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['opsInvoiceQueue'] });
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  const csv = async () => {
    setBusy(true);
    try {
      await downloadCsv(
        'ops/reports/invoice-queue',
        { days },
        'invoice-queue.csv',
      );
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const linkFor = (i: TInvoiceQueueItem) =>
    i.kind === 'program'
      ? `${PATH.opsPrograms}?id=${i.id}`
      : PATH.opsClassEdit.replace(':id', i.id);

  const section = (
    title: string,
    basis: string,
    list: { count: number; items: TInvoiceQueueItem[] },
  ) => (
    <Card
      title={`${title} · ${list.count}`}
      className="mb-4"
      right={<span className="text-xs text-gray-three">{basis}</span>}
    >
      {list.items.length === 0 ? (
        <EmptyState title="Nothing waiting" hint="Done when empty." />
      ) : (
        <div className="overflow-x-auto">
          <table className={`${tableClass} min-w-[900px]`}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Account</th>
                <th className={thClass}>Date</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={thClass}>Why it is here</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {list.items.map((i) => (
                <tr key={`${i.kind}-${i.id}`}>
                  <td className={`${tdClass} font-medium text-gray-four`}>
                    <Link className="hover:text-blue-primary" to={linkFor(i)}>
                      {i.name ?? i.id}
                    </Link>
                    <div className="text-xs text-gray-bold">
                      {i.classNumber ?? ''}
                      {i.classType ? ` · ${i.classType}` : ''}
                      {i.status ? ' · ' : ''}
                      {i.status && <Pill value={i.status} />}
                      {i.localOrigin && (
                        <span className="ml-1">
                          <OriginBadge local />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={tdClass}>{i.accountName ?? '—'}</td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    {i.date?.slice(0, 10) ?? '—'}
                  </td>
                  <td
                    className={`${tdClass} text-right [font-variant-numeric:tabular-nums]`}
                  >
                    {money(i.amount)}
                  </td>
                  <td className={`${tdClass} text-xs text-gray-three`}>
                    {i.reason}
                  </td>
                  <td className={`${tdClass} text-right`}>
                    {noteFor === `${i.kind}-${i.id}` ? (
                      <span className="flex items-center justify-end gap-1.5">
                        <input
                          className={`${inputClass} w-56`}
                          placeholder="Invoice number or note (optional)"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          autoFocus
                        />
                        <button
                          className={btnSecondary}
                          disabled={done.isPending}
                          onClick={() => done.mutate(i)}
                        >
                          {done.isPending ? 'Saving…' : 'Confirm'}
                        </button>
                        <button
                          className="text-xs text-gray-three"
                          onClick={() => {
                            setNoteFor(null);
                            setNote('');
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className={btnSecondary}
                        onClick={() => setNoteFor(`${i.kind}-${i.id}`)}
                      >
                        Mark invoiced
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <div className={labelClass}>Classes that ran in the last</div>
          <select
            className={inputClass}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {LOOKBACKS.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </div>
        <span className="flex-1" />
        <button className={btnSecondary} onClick={csv} disabled={busy || !data}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      {data && (
        <div className="mb-4 rounded-md bg-blue-100 px-4 py-2.5 text-sm text-blue-primary">
          {data.note}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-one">
          {resolveApiMessage(error)}
        </div>
      )}

      {isLoading || !data ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {section('Classes to invoice', data.basis.classes, data.classes)}
          {section('Travel to invoice', data.basis.travel, data.travel)}
          {section('Programs to invoice', data.basis.programs, data.programs)}
          <p className="text-xs text-gray-three">
            A class is "invoice directly" when its deal's payment type is
            Invoice or Purchase Order, or when a person sets the flag on the
            class. Marking a row done records who and when, and the note, on the
            record; it does not touch QuickBooks or Salesforce.
          </p>
        </>
      )}
    </>
  );
};

export default InvoiceQueue;
